import { Component } from '@theme/component';

class SanneverseSection extends Component {
  #raf = null;
  #scrollY = 0;
  #mouse = { x: 0.5, y: 0.5 };
  #target = { x: 0.5, y: 0.5 };
  #scheme = 'base';
  #fade = 0;

  connectedCallback() {
    super.connectedCallback();
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mq = window.matchMedia('(max-width: 860px)');
    this.mobile = this.mq.matches;

    this.cards = Array.from(this.querySelectorAll('.sv-card'));
    this.mark = this.querySelector('.sv__mark');
    this.intro = this.querySelector('.sv__intro');

    this.strength = (parseFloat(this.dataset.parallax) || 45) / 45;
    this.mouseWanted = this.dataset.mouseParallax === 'true';
    this.spinOn = this.dataset.spin === 'true';

    this.schemes = this.cards.map((c) => ({
      bg: c.dataset.hoverBg,
      mark: c.dataset.hoverMark,
      text: c.dataset.hoverText,
    }));

    this.onScroll = this.onScroll.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMq = this.onMq.bind(this);

    this.autoCanvas = this.dataset.canvasMode !== 'fixed';
    this.tail = parseFloat(this.dataset.canvasTail);
    if (!Number.isFinite(this.tail)) this.tail = 12;

    this.#applyScale();
    this.#fitCanvas();
    this.#fade = this.#fadeDistance();
    this.#observe();
    if (!this.mobile) this.#bindHovers();

    document.fonts?.ready.then(() => { this.#fitCanvas(); this.#fade = this.#fadeDistance(); });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.mq.addEventListener('change', this.onMq);
    if (!this.mobile && this.mouseWanted && !this.reduce) window.addEventListener('mousemove', this.onMove);
    this.onScroll();

    if (!this.reduce) {
      this.#raf = requestAnimationFrame(this.#tick);
      if (!this.mobile && this.dataset.autoScroll === 'true') this.#autoScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.io?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMove);
    this.mq.removeEventListener('change', this.onMq);
  }

  #fadeDistance() {
    const lockup = this.intro?.querySelector('.sv__intro-bottom') || this.intro;

    if (this.mobile) {
      const card = this.cards[0];
      if (!card || !lockup) return window.innerHeight * 0.16;
      const scrolled = window.scrollY || window.pageYOffset || 0;
      const cardTop = card.getBoundingClientRect().top + scrolled;
      return Math.max(48, cardTop - lockup.getBoundingClientRect().bottom);
    }

    const curve = window.innerHeight * 0.55;
    if (!lockup) return curve;

    // The lockup is fixed and paints above the cards, so it must be gone before
    // anything can pass under it. Never later than the curve, sooner if a card
    // that shares its horizontal band arrives first.
    return Math.max(48, Math.min(curve, this.#clearance(lockup)));
  }

  #clearance(lockup) {
    const scrolled = window.scrollY || window.pageYOffset || 0;
    const box = lockup.getBoundingClientRect();
    const marks = this.mark ? [this.mark.getBoundingClientRect()] : [];
    const parts = [...this.intro.querySelectorAll('.sv__intro-line, .sv__intro-eyebrow')]
      .map((el) => el.getBoundingClientRect())
      .concat(marks);
    if (!parts.length) parts.push(box);

    const left = Math.min(...parts.map((r) => r.left));
    const right = Math.max(...parts.map((r) => r.right));
    const bottom = Math.max(...parts.map((r) => r.bottom));

    let nearest = Infinity;
    for (const card of this.cards) {
      const r = card.getBoundingClientRect();
      if (r.right <= left || r.left >= right) continue;
      nearest = Math.min(nearest, r.top + scrolled - bottom);
    }
    return nearest;
  }

  onScroll() {
    this.#scrollY = window.scrollY || window.pageYOffset;
    const d = this.#fade > 0 ? this.#fade : this.#fadeDistance();
    const fade = Math.max(0, Math.min(1, this.#scrollY / d));
    if (this.intro) {
      this.intro.style.opacity = String(1 - fade);
      if (!this.mobile) this.intro.style.transform = `translateY(${(-fade * 12 - 28).toFixed(2)}px)`;
    }
  }

  onMove(e) {
    this.#target = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight };
  }

  onResize() {
    this.#applyScale();
    this.#fitCanvas();
    this.#fade = this.#fadeDistance();
  }

  onMq(e) {
    this.mobile = e.matches;
    for (const card of this.cards) {
      card.style.width = '';
      card.style.transform = '';
      const media = card.querySelector('.sv-card__media');
      if (media) { media.style.width = ''; media.style.height = ''; }
    }
    if (this.mark) this.mark.style.transform = '';
    if (this.intro) this.intro.style.transform = '';
    this.#scheme = 'base';
    this.style.removeProperty('--sv-bg');
    this.style.removeProperty('--sv-mark-color');
    this.style.removeProperty('--sv-ink');
    this.style.removeProperty('--sv-sub');
    if (!this.mobile) { this.#applyScale(); this.#bindHovers(); }
    this.#fitCanvas();
    this.#fade = this.#fadeDistance();
    this.onScroll();
  }

  #fitCanvas() {
    if (!this.autoCanvas) return;

    if (this.mobile) {
      this.style.setProperty('--sv-canvas-mobile', '0px');
      return;
    }

    const vh = window.innerHeight;
    if (!this.cards.length) {
      this.style.setProperty('--sv-canvas-desktop', `${Math.round(vh * 1.6)}px`);
      return;
    }

    let need = 0;
    for (const card of this.cards) {
      const bottom = card.offsetTop + card.offsetHeight;
      const k = ((parseFloat(card.dataset.speed) || 0) / 100) * this.strength;
      const drifted = k < 1 ? (bottom - vh * k) / (1 - k) : bottom;
      need = Math.max(need, bottom, drifted);
    }

    const h = Math.max(need + vh * (this.tail / 100), vh * 1.6);
    this.style.setProperty('--sv-canvas-desktop', `${Math.round(h)}px`);
  }

  #applyScale() {
    if (this.mobile) return;
    const scale = Math.max(0.6, Math.min(1.12, window.innerWidth / 1440));
    for (const card of this.cards) {
      const w = Math.round((parseFloat(card.dataset.w) || 480) * scale);
      const h = Math.round((parseFloat(card.dataset.h) || 740) * scale);
      card.style.width = `${w}px`;
      const media = card.querySelector('.sv-card__media');
      if (media) { media.style.width = `${w}px`; media.style.height = `${h}px`; }
    }
  }

  #bindHovers() {
    if (this.hoversBound) return;
    this.hoversBound = true;
    this.cards.forEach((card) => {
      card.addEventListener('mouseenter', () => {
        if (this.mobile || !card.dataset.hoverBg) return;
        this.style.setProperty('--sv-bg', card.dataset.hoverBg);
        this.style.setProperty('--sv-mark-color', card.dataset.hoverMark);
        this.style.setProperty('--sv-ink', card.dataset.hoverText);
        this.style.setProperty('--sv-sub', card.dataset.hoverText);
      });
      card.addEventListener('mouseleave', () => {
        if (this.mobile) return;
        this.style.removeProperty('--sv-bg');
        this.style.removeProperty('--sv-mark-color');
        this.style.removeProperty('--sv-ink');
        this.style.removeProperty('--sv-sub');
      });
    });
  }

  #observe() {
    if (!window.IntersectionObserver) {
      this.cards.forEach((c) => c.classList.add('in'));
      return;
    }
    let count = 0;
    const armedAt = performance.now() + 700;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const initial = performance.now() - armedAt < 1200;
        const inner = e.target.querySelector('.sv-card__inner');
        if (inner) inner.style.transitionDelay = initial ? `${Math.min(count, 6) * 120}ms` : '0ms';
        e.target.classList.add('in');
        count += 1;
        this.io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    this.cards.forEach((c) => this.io.observe(c));
  }

  #applyScheme(s) {
    if (s === 'base') {
      this.style.removeProperty('--sv-bg');
      this.style.removeProperty('--sv-mark-color');
      this.style.removeProperty('--sv-ink');
      this.style.removeProperty('--sv-sub');
      return;
    }
    this.style.setProperty('--sv-bg', s.bg);
    this.style.setProperty('--sv-mark-color', s.mark);
    this.style.setProperty('--sv-ink', s.text);
    this.style.setProperty('--sv-sub', s.text);
  }

  #tick = () => {
    const sy = this.#scrollY;

    if (this.mobile) {
      if (this.mark && this.spinOn) this.mark.style.transform = `rotate(${(sy * 0.12).toFixed(2)}deg)`;
      const vh = window.innerHeight;
      if (sy < vh * 0.16) {
        if (this.#scheme !== 'base') { this.#applyScheme('base'); this.#scheme = 'base'; }
      } else {
        const c = vh * 0.5;
        let best = 0, bestD = Infinity;
        this.cards.forEach((el, i) => {
          const b = el.getBoundingClientRect();
          const d = Math.abs(b.top + b.height / 2 - c);
          if (d < bestD) { bestD = d; best = i; }
        });
        if (this.#scheme !== best) { this.#applyScheme(this.schemes[best]); this.#scheme = best; }
      }
      this.#raf = requestAnimationFrame(this.#tick);
      return;
    }

    this.#mouse.x += (this.#target.x - this.#mouse.x) * 0.06;
    this.#mouse.y += (this.#target.y - this.#mouse.y) * 0.06;
    const mx = this.#mouse.x - 0.5;
    const my = this.#mouse.y - 0.5;
    const mAmt = this.mouseWanted ? 1 : 0;

    for (const card of this.cards) {
      const speed = (parseFloat(card.dataset.speed) || 6) / 100;
      const par = sy * speed * this.strength;
      const depth = 8 + Math.abs(speed) * 90;
      const tx = mx * depth * mAmt;
      const ty = my * depth * 0.5 * mAmt;
      card.style.transform = `translate3d(${tx.toFixed(2)}px, ${(par + ty).toFixed(2)}px, 0)`;
    }

    if (this.mark) {
      const rot = this.spinOn ? sy * 0.08 : 0;
      this.mark.style.transform = `translate3d(${(-mx * 14 * mAmt).toFixed(2)}px, ${(-my * 14 * mAmt).toFixed(2)}px, 0) rotate(${rot.toFixed(2)}deg)`;
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };

  #autoScroll() {
    const speed = 14;
    const startDelay = 2200;
    let startT = null, lastT = null, pos = null, stopped = false, paused = false, raf;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((ev) => window.removeEventListener(ev, stop));
    };
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((ev) => window.addEventListener(ev, stop, { passive: true }));

    this.addEventListener('mouseover', (e) => { if (e.target.closest('.sv-card')) paused = true; });
    this.addEventListener('mouseout', (e) => { if (e.target.closest('.sv-card')) paused = false; });

    const step = (t) => {
      if (stopped) return;
      if (startT === null) { startT = t; lastT = t; }
      const dt = t - lastT;
      lastT = t;
      if (t - startT > startDelay && !paused) {
        if (pos === null) pos = window.scrollY || window.pageYOffset;
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        pos += speed * (dt / 1000);
        if (pos >= maxY) { window.scrollTo(0, maxY); stop(); return; }
        window.scrollTo(0, pos);
      } else if (paused) {
        pos = window.scrollY || window.pageYOffset;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }
}

if (!customElements.get('sanneverse-section')) {
  customElements.define('sanneverse-section', SanneverseSection);
}
